<script setup lang="ts">
import {
  PBtnMenu,
  PGlobalMenu,
  PGlobalMenuItem,
  PIcon,
  PMaxLines,
  PPromotedBadge,
  PSpace,
  PSpaceSize,
  PTextLink,
  PTheme,
  usePrevioDS,
} from '@previo/design-system'
import { ref, watch } from 'vue'
import { Color } from '@previo/codebooks'
import AppMenuBar from './AppMenuBar.vue'
import UserOptionsList from './components/UserOptionsList.vue'
import { modules } from './modules'
import PropertyItem from './components/PropertyItem.vue'
import SideMenuUser from './components/SideMenuUser.vue'
import { Routes } from '~/router/routes'
import { useUserStore } from '~/store/user'
import router from '~/router'
import { ModuleCheck } from '~/composables/usePermissionCheck'
import { Module } from '~/codebooks/acl/Module'
import mobileSlash from '~/assets/images/svg/mobile-slash.svg'
import { usePushNotifications } from '~/plugins/pushNotifications'

const pushNotifications = usePushNotifications()
const pushNotificationsClient = usePushNotifications().instance
const userStore = useUserStore()

const ds = usePrevioDS()
const openMenu = ref<boolean>(false)

const closeMenu = (): void => {
  openMenu.value = false
}

const isMobileFirst = (item: PGlobalMenuItem): boolean => {
  const route = router.resolve({ name: item.route })
  return route.meta.isMobileFirst ?? true
}

const stopWatch = watch(
  pushNotifications.isReady,
  async (isReady: boolean): Promise<void> => {
    if (!isReady) {
      return
    }

    const isSubscribed = await pushNotificationsClient.isSubscribed()
    const isDenied = await pushNotificationsClient.isDeniedByBrowser()

    if (isDenied) {
      return stopWatch()
    }

    if (isSubscribed) {
      await pushNotificationsClient.activate(userStore.user.id!)

      return stopWatch()
    }

    if (!pushNotificationsClient.canAutoRequestPermission()) {
      return stopWatch()
    }

    await pushNotificationsClient.start(userStore.user.id!)

    return stopWatch()
  },
  { immediate: true },
)
</script>

<template>
  <app-menu-bar>
    <template v-if="ds.screen().isMobile" #start>
      <property-item />
    </template>

    <p-space :mobile="PSpaceSize.SM" :laptop="PSpaceSize.LG" />

    <template v-if="ds.screen().isLaptopAndLarge">
      <template v-if="ModuleCheck.hasAccess(Module.RESERVATION_CANVAS)">
        <router-link :to="{ name: Routes.CALENDAR_INDEX_P1_P2 }">
          <p-btn-menu :theme="PTheme.DARK" outline icon-start="calendar-lines" cy="calendar" />
        </router-link>

        <p-space :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.XS" />
      </template>

      <template v-if="ModuleCheck.hasAccess(Module.RESERVATION_TABLE)">
        <router-link :to="{ name: Routes.RESERVATIONS_INDEX_P1_P2 }">
          <p-btn-menu :theme="PTheme.DARK" outline icon-start="list" cy="reservation" />
        </router-link>

        <p-space :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.XS" />
      </template>

      <router-link :to="{ name: Routes.DASHBOARD_INDEX }">
        <p-btn-menu :theme="PTheme.DARK" outline icon-start="gauge" cy="dashboard" />
      </router-link>
    </template>

    <p-space :mobile="PSpaceSize.GROW" :laptop="PSpaceSize.MD" />

    <p-global-menu v-model:open-mobile-menu="openMenu" :items="modules" @close-mobile="closeMenu">
      <template #side-menu-actions>
        <router-link
          v-if="ModuleCheck.hasAccess(Module.RESERVATION_TABLE)"
          :to="{ name: Routes.RESERVATIONS_INDEX_P1_P2 }"
          class="p-col"
          @click="closeMenu"
        >
          <p-btn-menu outline grow icon-start="list" cy="reservation" />
        </router-link>

        <template v-if="ModuleCheck.hasAccess(Module.REPORTS_DASHBOARD)">
          <p-space v-if="ModuleCheck.hasAccess(Module.RESERVATION_TABLE)" :mobile="PSpaceSize.SM" />

          <router-link :to="{ name: Routes.DASHBOARD_INDEX }" class="p-col" @click="closeMenu">
            <p-btn-menu outline grow icon-start="gauge" cy="dashboard" />
          </router-link>
        </template>
      </template>

      <template #side-menu-item="{ item }: { item: PGlobalMenuItem }">
        <p-icon v-if="item.icon" :icon="item.icon" :color="Color.PRIMARY" />
        <p-space :mobile="PSpaceSize.SM" />

        <p-text-link :text="item.title" :color="Color.GREY_600" :max-lines="PMaxLines.ONE" />

        <template v-if="!isMobileFirst(item)">
          <p-space :mobile="PSpaceSize.XS" />
          <img :src="mobileSlash" width="18" height="18" />
        </template>

        <template v-if="item.isPromoted">
          <p-space :mobile="PSpaceSize.XS" />
          <p-promoted-badge />
        </template>

        <p-space :mobile="PSpaceSize.GROW" />
      </template>

      <template #side-menu-footer>
        <side-menu-user @navigate="closeMenu" />
      </template>
    </p-global-menu>

    <p-space :mobile="PSpaceSize.GROW" />

    <template v-if="ds.screen().isLaptopAndLarge">
      <p-space :mobile="PSpaceSize.SM" />

      <property-item />
    </template>

    <template #user-options>
      <user-options-list />
    </template>

    <template v-if="ds.screen().isMobile" #end>
      <template v-if="ModuleCheck.hasAccess(Module.RESERVATION_CANVAS)">
        <p-space :mobile="PSpaceSize.SM" />
        <router-link :to="{ name: Routes.CALENDAR_INDEX_P1_P2 }">
          <p-btn-menu :theme="PTheme.DARK" outline icon-start="calendar-lines" cy="calendar" />
        </router-link>
      </template>

      <p-space :mobile="PSpaceSize.SM" />
      <p-btn-menu
        :theme="PTheme.DARK"
        outline
        icon-start="bars"
        cy="open-or-close-menu"
        @click="openMenu = !openMenu"
      />
    </template>
  </app-menu-bar>
</template>
